import { nativeInvoicePayContract } from "@common/contracts/native-invoice-pay.contract";
import { NativeInvoicePayResponse } from "@common/schemas/domains/invoice/native-invoice-pay.schema";
import { useQuery } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const nativeInvoicePayApiClient = getBackendAPIClient(nativeInvoicePayContract);

export const nativeInvoicePayQueryKey = (paymentToken: string) => [
    "native-invoice-pay",
    paymentToken,
];

export interface NativeInvoicePayResult {
    found: boolean;
    page: NativeInvoicePayResponse | null;
}

async function fetchPaymentPage(
    paymentToken: string,
): Promise<NativeInvoicePayResult> {
    const response = await nativeInvoicePayApiClient.getPaymentPage({
        params: { paymentToken },
    });
    if (response.status === 200) {
        return { found: true, page: response.body };
    }
    // Any 4xx is terminal — the token is genuinely missing/expired (404) or
    // malformed (400, e.g. a non-uuid paymentToken rejected at the contract
    // boundary). Retrying can't fix a client error, so resolve to the permanent
    // "invalid or expired" screen. Only a 5xx is transient, so throw to surface
    // a retriable error state.
    if (response.status >= 400 && response.status < 500) {
        return { found: false, page: null };
    }
    throw new Error(`Unexpected pay-page status ${response.status}`);
}

// Public, unauthenticated pay-page lookup. A 4xx resolves to `found: false` (a
// missing/malformed token is terminal, not transient); a transient 5xx throws
// and is retried a couple of times before surfacing an error state.
export function useNativeInvoicePayQuery(paymentToken: string) {
    return useQuery({
        queryKey: nativeInvoicePayQueryKey(paymentToken),
        queryFn: async () => await fetchPaymentPage(paymentToken),
        retry: 2,
        refetchOnWindowFocus: false,
    });
}
